/**
 * Task history's rule-assessment pill and date-cell routing; resolution dates render independently of assessment.
 */
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { DomainType, MetadataEntityFieldData, ServiceHistoryRow } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { BadgeComponent, SeverityBadgeDirective } from '@workbuddy/ui/components/badge';
import { WeekdayDateCellRenderer } from '../../shared/weekday.date.cell.renderer';
import { serviceHistoryResultPill, ServiceHistoryResultPill } from './service.history.result';
import { PortalHistoryJobCellRenderer } from './portal.job.cell.renderer';

/** Params the board passes: the `result` field's metadata, whose enum options hold the severities. */
export interface ServiceHistoryResultCellParams extends ICellRendererParams<ServiceHistoryRow> {
  field: MetadataEntityFieldData;
}

/** Mounted by the Task history boards' `overrideCellRenderer` on the `result` column. */
@Component({
  selector: 'service-history-result-cell-renderer',
  standalone: true,
  imports: [BadgeComponent, SeverityBadgeDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: flex; align-items: center; gap: 8px; min-width: 0; height: 100%; overflow: hidden;' },
  template: `
    @if ($pill(); as pill) {
      <w-badge size="small" [severityBadge]="pill.severity" [severityBadgeLabel]="pill.label" [tooltip]="pill.tooltip" [onlyShowTooltipIfOverflow]="pill.tooltip === pill.label"></w-badge>
    }
  `,
})
export class ServiceHistoryResultCellRenderer implements ICellRendererAngularComp {

  protected readonly $pill = signal<ServiceHistoryResultPill | null>(null);

  public agInit(params: ServiceHistoryResultCellParams): void {

    const pill = serviceHistoryResultPill(params.data, params.field);
    this.$pill.set(pill);
  }

  public refresh(params: ServiceHistoryResultCellParams): boolean {

    this.agInit(params);
    return true;
  }
}

/**
 * The boards' `table.overrideCellRenderer`: swaps the plain enum pill on `result` for the detailed one, and names
 * the weekday on the Date, Due and Resolution Date columns.
 */
export function serviceHistoryCellRenderer(_context: unknown, _metadata: unknown, field: MetadataEntityFieldData, renderer: Record<string, unknown>): Record<string, unknown> {

  switch (field.name) {
    case 'job': return ConfigService.environment.domainType === DomainType.ClientPortal ? { ...renderer, cellRenderer: PortalHistoryJobCellRenderer } : renderer;
    case 'result': return { ...renderer, cellRenderer: ServiceHistoryResultCellRenderer, cellRendererParams: { ...(renderer.cellRendererParams as object | undefined), field } };
    case 'date':
    case 'rescheduledDate':
    case 'dueDate': return { ...renderer, cellRenderer: WeekdayDateCellRenderer };
    case 'completedOn': return { ...renderer, cellRenderer: WeekdayDateCellRenderer };
    default: return renderer;
  }
}
